import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * A horizontally scrolling box that says so: a fade on the edge that still has
 * content (unless `pinnedEnd`, where a sticky column owns that edge) and, on a
 * phone, a one-line hint while there is more to the right.
 */
export function ScrollFade({ children, pinnedEnd = false }: { children: ReactNode; pinnedEnd?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const [more, setMore] = useState(false);
  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setMore(el.scrollWidth - el.clientWidth - el.scrollLeft > 4);
  }, []);
  useEffect(() => {
    measure();
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  }, [measure]);
  return (
    <div>
      <div className="relative">
        <div ref={ref} onScroll={measure} className="overflow-x-auto rounded-card border border-border">
          {children}
        </div>
        {more && !pinnedEnd && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-px right-px w-8 rounded-r-card bg-gradient-to-l from-background to-transparent"
          />
        )}
      </div>
      {more && <p className="mt-1 text-meta text-fg-hint sm:hidden">Swipe sideways for more columns</p>}
    </div>
  );
}
